/**
 * Mecanismo de retry automático para requisições HTTP
 * Implementa backoff exponencial e jitter para evitar sobrecarga
 */

import { ErrorType } from "./errorHandler";
import { logger } from "./logger";

export interface RetryConfig {
  maxRetries: number;
  initialDelay: number; // em ms
  maxDelay: number; // em ms
  backoffMultiplier: number;
  retryableStatuses: number[];
  retryableErrors: ErrorType[];
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 2, // Reduzido de 3 para 2
  initialDelay: 1000, // 1 segundo
  maxDelay: 10000, // Reduzido de 30s para 10s
  backoffMultiplier: 1.5, // Reduzido de 2 para 1.5 (menos agressivo)
  retryableStatuses: [500, 502, 503, 504, 408], // Server errors, timeout (429 removido para tratamento específico)
  retryableErrors: [ErrorType.NETWORK, ErrorType.TIMEOUT, ErrorType.SERVER],
};

export class RetryMechanism {
  private config: RetryConfig;

  constructor(config: Partial<RetryConfig> = {}) {
    this.config = { ...DEFAULT_RETRY_CONFIG, ...config };
  }

  /**
   * Calcula o delay para a próxima tentativa usando backoff exponencial com jitter
   */
  private calculateDelay(attempt: number): number {
    const baseDelay =
      this.config.initialDelay *
      Math.pow(this.config.backoffMultiplier, attempt - 1);
    const jitter = Math.random() * 0.1 * baseDelay; // 10% de jitter
    const delay = Math.min(baseDelay + jitter, this.config.maxDelay);

    return Math.floor(delay);
  }

  /**
   * Verifica se uma resposta HTTP deve ser tentada novamente
   */
  private shouldRetryResponse(response: Response): boolean {
    return this.config.retryableStatuses.includes(response.status);
  }

  /**
   * Verifica se um erro deve ser tentado novamente
   */
  private shouldRetryError(error: any): boolean {
    if (!error) return false;

    // Erros de autenticação NÃO devem ser retry (401, 403)
    if (error.status && (error.status === 401 || error.status === 403)) {
      return false;
    }

    // Erros de rede (exceto autenticação)
    if (error.name === "TypeError" && error.message?.includes("fetch")) {
      return true;
    }

    // Timeout
    const isTimeout =
      error.name === "AbortError" ||
      (error.message && error.message.toLowerCase().includes("timeout"));

    if (isTimeout) {
      return true;
    }

    // Erros de resposta HTTP
    if (error.status && this.config.retryableStatuses.includes(error.status)) {
      return true;
    }

    return false;
  }

  /**
   * Executa uma função com retry automático
   */
  async executeWithRetry<T>(
    operation: () => Promise<T>,
    context?: string,
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 1; attempt <= this.config.maxRetries + 1; attempt++) {
      try {
        const result = await operation();

        // Se chegou aqui, a operação foi bem-sucedida
        if (attempt > 1) {
          logger.info("Operation succeeded after retry", { attempt, context });
        }

        return result;
      } catch (error) {
        lastError = error;

        // Verifica se deve tentar novamente
        const shouldRetry =
          attempt <= this.config.maxRetries && this.shouldRetryError(error);

        if (!shouldRetry) {
          // Não deve tentar novamente, lança o erro
          throw error;
        }

        // Calcula delay para próxima tentativa
        const delay = this.calculateDelay(attempt);

        logger.warn("Retry attempt failed, will retry", {
          attempt,
          maxRetries: this.config.maxRetries,
          delay,
          context,
          error: (error as any)?.message || String(error),
        });

        // Espera antes da próxima tentativa
        await this.delay(delay);
      }
    }

    // Se chegou aqui, todas as tentativas falharam
    throw lastError;
  }

  /**
   * Wrapper específico para fetch requests
   */
  async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    context?: string,
  ): Promise<Response> {
    return this.executeWithRetry(async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Verifica se deve tentar novamente baseado no status
        if (this.shouldRetryResponse(response)) {
          const error = new Error(
            `HTTP ${response.status}: ${response.statusText}`,
          );
          (error as any).status = response.status;
          throw error;
        }

        return response;
      } catch (error) {
        clearTimeout(timeoutId);

        if ((error as any).name === "AbortError") {
          throw new Error("Request timeout");
        }

        throw error;
      }
    }, context);
  }

  /**
   * Utilitário para delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Atualiza configuração de retry
   */
  updateConfig(config: Partial<RetryConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Instância global do mecanismo de retry
export const retryMechanism = new RetryMechanism();
